import DOMPurify from 'dompurify';

/**
 * Security utility for sanitizing user input to prevent XSS attacks
 */

/**
 * Sanitize HTML content to remove potentially dangerous scripts
 * Use this for any user-generated content that will be displayed as HTML
 */
export function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
}

/**
 * Sanitize text content (strips all HTML tags)
 * Use this for plain text fields like names, titles, etc.
 */
export function sanitizeText(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
}

/**
 * Sanitize and limit text length
 * Prevents buffer overflow attacks and ensures reasonable input size
 */
export function sanitizeAndTrim(dirty: string, maxLength: number = 1000): string {
  const sanitized = sanitizeText(dirty);
  return sanitized.substring(0, maxLength);
}

/**
 * Validate and sanitize email addresses
 */
export function sanitizeEmail(email: string): string {
  const sanitized = sanitizeText(email.toLowerCase().trim());
  // Additional email validation can be added here
  return sanitized;
}

/**
 * Sanitize URLs to prevent javascript: protocol attacks
 */
export function sanitizeUrl(url: string): string {
  const sanitized = sanitizeText(url.trim());
  
  // Block dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  const lowerUrl = sanitized.toLowerCase();
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return '#'; // Return safe fallback
    }
  }
  
  return sanitized;
}

/**
 * Sanitize file names to prevent path traversal attacks
 */
export function sanitizeFileName(fileName: string): string {
  // Remove path separators and null bytes
  let sanitized = fileName.replace(/[\/\\:*?"<>|\0]/g, '_');
  
  // Remove leading dots (hidden files) and spaces
  sanitized = sanitized.replace(/^[\s.]+/, '');
  
  // Limit length
  sanitized = sanitized.substring(0, 255);
  
  return sanitized || 'unnamed_file';
}

/**
 * Sanitize phone numbers (keep only digits and common formatting chars)
 */
export function sanitizePhone(phone: string): string {
  return phone.replace(/[^\d\s()+-]/g, '').substring(0, 20);
}
